import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Package, BarChart3, PieChart } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

export default function AdvancedReports() {
  const [period, setPeriod] = useState('30d');
  const [revenueData, setRevenueData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [revenue, customers] = await Promise.all([
        api.get(`/tenant/reports/revenue?period=${period}`),
        api.get('/tenant/reports/customers')
      ]);
      setRevenueData(revenue.data);
      setCustomerData(customers.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="advanced-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Advanced Reports</h1>
          <p className="text-muted-foreground">Analitik lengkap toko Anda</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 Hari</SelectItem>
            <SelectItem value="30d">30 Hari</SelectItem>
            <SelectItem value="90d">90 Hari</SelectItem>
            <SelectItem value="1y">1 Tahun</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="revenue">
        <TabsList>
          <TabsTrigger value="revenue" data-testid="tab-revenue">
            <DollarSign className="h-4 w-4 mr-2" /> Revenue
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Users className="h-4 w-4 mr-2" /> Customers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Rp {fmt(revenueData?.total_revenue)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{fmt(revenueData?.total_orders)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Average Order Value</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Rp {fmt(revenueData?.average_order_value)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Period</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{period === '7d' ? '7 Hari' : period === '30d' ? '30 Hari' : period === '90d' ? '90 Hari' : '1 Tahun'}</div>
              </CardContent>
            </Card>
          </div>

          {/* Chart Area */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" /> Revenue Chart
              </CardTitle>
              <CardDescription>Grafik pendapatan per {period === '90d' ? 'minggu' : period === '1y' ? 'bulan' : 'hari'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end gap-1">
                {revenueData?.chart_data?.map((item, idx) => {
                  const maxRevenue = Math.max(...revenueData.chart_data.map(d => d.revenue || 0));
                  const height = maxRevenue > 0 ? ((item.revenue || 0) / maxRevenue) * 100 : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-primary rounded-t transition-all hover:bg-primary/80" 
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`Rp ${fmt(item.revenue)} - ${item._id}`}
                      />
                      {revenueData.chart_data.length <= 10 && (
                        <span className="text-[10px] text-muted-foreground mt-1 truncate w-full text-center">
                          {item._id?.split('-').pop()}
                        </span>
                      )}
                    </div>
                  );
                })}
                {(!revenueData?.chart_data || revenueData.chart_data.length === 0) && (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    Belum ada data untuk periode ini
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Products */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" /> Top Products
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {revenueData?.top_products?.slice(0, 5).map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{idx + 1}</Badge>
                        <span className="text-sm truncate max-w-[200px]">{p.product_name}</span>
                      </div>
                      <span className="text-sm font-medium">Rp {fmt(p.revenue)}</span>
                    </div>
                  ))}
                  {(!revenueData?.top_products || revenueData.top_products.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">Belum ada data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" /> Revenue by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {revenueData?.revenue_by_category?.slice(0, 5).map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm">{c._id || 'Uncategorized'}</span>
                      <span className="text-sm font-medium">Rp {fmt(c.revenue)}</span>
                    </div>
                  ))}
                  {(!revenueData?.revenue_by_category || revenueData.revenue_by_category.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">Belum ada data</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          {/* Customer Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{fmt(customerData?.total_customers)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">New This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">+{fmt(customerData?.new_customers_this_month)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Repeat Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{customerData?.repeat_customer_rate || 0}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Avg. Lifetime Value</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">-</div>
              </CardContent>
            </Card>
          </div>

          {/* Top Customers */}
          <Card>
            <CardHeader>
              <CardTitle>Top Customers by Spending</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-muted-foreground">Customer</th>
                    <th className="text-left py-2 text-sm font-medium text-muted-foreground">Email</th>
                    <th className="text-right py-2 text-sm font-medium text-muted-foreground">Total Spent</th>
                    <th className="text-right py-2 text-sm font-medium text-muted-foreground">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {customerData?.top_customers?.map((c, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-3">{c.name}</td>
                      <td className="py-3 text-muted-foreground">{c.email}</td>
                      <td className="py-3 text-right font-medium">Rp {fmt(c.total_spent)}</td>
                      <td className="py-3 text-right">{c.total_orders || 0}</td>
                    </tr>
                  ))}
                  {(!customerData?.top_customers || customerData.top_customers.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">Belum ada data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Customer Growth */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Growth (Last 6 Months)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-end gap-4">
                {customerData?.customer_growth?.map((item, idx) => {
                  const maxCount = Math.max(...customerData.customer_growth.map(d => d.count || 0));
                  const height = maxCount > 0 ? ((item.count || 0) / maxCount) * 100 : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <span className="text-sm font-medium mb-1">{item.count}</span>
                      <div 
                        className="w-full bg-green-500 rounded-t transition-all hover:bg-green-400" 
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-muted-foreground mt-2">{item._id}</span>
                    </div>
                  );
                })}
                {(!customerData?.customer_growth || customerData.customer_growth.length === 0) && (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    Belum ada data
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
